"use client";

import * as React from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Dialog, DialogContent, DialogTitle } from "./dialog";
import { H3 } from "./h3";
import { Icon, isIconName } from "./icon";
import { VisuallyHidden } from "./visually-hidden";

export interface OnboardingDialogProps
  extends React.ComponentProps<typeof Dialog> {
  iconSrc: string;
  title: string;
  children: React.ReactNode;
  icon: string;
  primaryCta: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export const OnboardingDialog = React.forwardRef<
  HTMLDivElement,
  OnboardingDialogProps
>(
  (
    {
      open,
      onOpenChange,
      iconSrc,
      title,
      children,
      primaryCta,
      className,
      icon,
      ...props
    },
    ref,
  ) => {
    return (
      <Dialog open={open} onOpenChange={onOpenChange} {...props}>
        <DialogContent
          ref={ref}
          className={cn(
            "w-full max-w-[451px] rounded-3xl bg-white p-8 opacity-90",
            className,
          )}
        >
          <div className="flex flex-col items-center space-y-4">
            {icon && isIconName(icon) && (
              <div className="bg-brand-greens-light flex h-[96px] w-[96px] items-center justify-center rounded-full">
                <Icon name={icon} className="h-[64px] w-[64px] opacity-[0.1]" />
              </div>
            )}

            <VisuallyHidden>
              <DialogTitle>{title}</DialogTitle>
            </VisuallyHidden>
            <div className="w-full text-center">
              <H3 className="text-brand-secondary-dark text-[40px] leading-[120%] tracking-normal">
                {title}
              </H3>
            </div>

            <div className="text-brand-moss-greens-90 text-center leading-[140%]">
              {children}
            </div>

            <div className="flex w-full flex-col items-center">
              <Button className="px-8 py-4" onClick={primaryCta.onClick}>
                {primaryCta.label}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);

OnboardingDialog.displayName = "OnboardingDialog";
